import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { colors, fonts, glyphs, useMediaQuery } from "../../../Styles";
import { PrimaryPopover } from "../..";
import { ButtonTypes, Pen } from "../../../Definitions";
import { getBorderRadius } from "../../_Components";
import {  useDeviceInfo } from "../../../Services";
import { useModalContext } from "./_Modals/newSectionModalContext";
import NewButton from "../../BaseComponents/newButton";

export const parseGridArea = (area: string): [number, number] => {
  const [rowStart, colStart] = area.split("/").map(str => parseInt(str, 10));
  return [rowStart - 1, colStart - 1]; // 0-indekseret
};

const NewSectionModalLvl3: React.FC = () => {
  const { state: { farm, domain, connection, error, section }, dispatch } = useModalContext()
  const { t } = useTranslation()
  const mobileXs = useMediaQuery(`(max-width: 805px)`);
  const [, setIsOpen] = useState<boolean>(false)
  const [isExpanded, setExpand] = useState<boolean>(false)

  const pens = { ...section, contents: section.contents as Pen[] };
  const { data: deviceInfoState } = useDeviceInfo(connection);

  // Get devices to find i/o name
  const deviceInfo = useMemo(() => {
    if (deviceInfoState[domain]) {
      return deviceInfoState[domain].data.map((deviceInfo) => ({id: deviceInfo.device_id, name: deviceInfo.name, systemId: deviceInfo.system_assignment, type: 1}))
    }
    return []
  }, [deviceInfoState, domain])

  // #region | BorderRadius
  // #endregion

  // #region | Pen View
  const penView = useMemo(() => {
    const views: JSX.Element[] = [];
  
    Array.from(pens.contents).forEach((currentPen) => {
      const warnings = [];
      const [row, col] = currentPen.visual ? parseGridArea(currentPen.visual) : [0, 0];
      const borderRadius = !mobileXs ? getBorderRadius({ row, col, columns: section.columns, rows: section.rows }) : undefined;
  
      const penElement: JSX.Element[] = [];
      const popOverElement: JSX.Element[] = [];
  
      const name = currentPen.name ?? "";
      penElement.push(
        <div key={'name_' + currentPen.penId} style={{whiteSpace: "nowrap", gridArea: "1/1/2/2", display: "flex", alignItems: "center", gap: "5px", ...fonts.fontSize4, ...fonts.paragraph, color: colors.greyDarkPrimary}}>
          {`${t("locationFeederPages.locationFeeder.pen", { defaultValue: "Pen" })}: `} <b>{name}</b>
        </div>
      );
  
      
      const ioBoardName = deviceInfo.find((io) => io.id === currentPen.io)?.name ?? "";
      if (section.columns >= 3) popOverElement.push(
        <div key={'ioBoard_' + currentPen.penId} style={{whiteSpace: "nowrap", color: isExpanded ? colors.greyDarkPrimary : colors.greyLightTertiary, ...fonts.paragraph, ...fonts.fontSize4}}>
          {`${t("devices.aicoIo", { defaultValue: "I/O" })}: `} <b>{ioBoardName}</b>
        </div>
      )
      if (section.columns < 3) penElement.push(
        <div key={'ioBoard_' + currentPen.penId} style={{whiteSpace: "nowrap", gridArea: "1/2/2/3", display: "flex", alignItems: "center", gap: "5px", ...fonts.fontSize4, ...fonts.paragraph, color: colors.greyDarkPrimary}}>
          {`${t("devices.aicoIo", { defaultValue: "I/O" })}: `} <b>{ioBoardName}</b>
        </div>
      )
  
      popOverElement.push(
        <p key={'feederOutput_' + currentPen.penId} style={{ whiteSpace: "nowrap", color: isExpanded ? colors.greyDarkPrimary : colors.greyLightTertiary, ...fonts.paragraph, ...fonts.fontSize4 }}>
          {`${t("locationFeederPages.sections.feederOutput", {defaultValue: "Feeder Output"})}:`} <b>{currentPen.feederOutput ?? ""}</b>
        </p>
      );

      if (currentPen.waterOutput != null)
      popOverElement.push(
        <p key={'waterOutput_' + currentPen.penId} style={{ whiteSpace: "nowrap", color: isExpanded ? colors.greyDarkPrimary : colors.greyLightTertiary, ...fonts.paragraph, ...fonts.fontSize4 }}>
          {`${t("locationFeederPages.sections.waterOutput", { defaultValue: "Water Output" })}:`} <b>{currentPen.waterOutput ?? ""}</b>
        </p>
      );

      if (currentPen.activatorInput != null)
      popOverElement.push(
        <p key={'activatorInput_' + currentPen.penId} style={{ whiteSpace: "nowrap", color: isExpanded ? colors.greyDarkPrimary : colors.greyLightTertiary, ...fonts.paragraph, ...fonts.fontSize4 }}>
          {`${t("locationFeederPages.sections.activatorInput", { defaultValue: "Activator Input" })}:`} <b>{currentPen.activatorInput ?? ""}</b>
        </p>
      );
     
      if (warnings.length) {
        penElement.push(
         <div key={'warningLabel_'+currentPen.penId} style={{whiteSpace: "nowrap", gridArea: "1/3/2/4", display: "flex", alignItems: "center", justifyContent: "center", gap: "5px", ...fonts.fontSize4, ...fonts.paragraph }}>
            <glyphs.Empty fillColor={colors.systemRed} />
         </div> 
        )
      }
  
      views.push(
        <div key={'pen_' + currentPen.penId} style={{
          gridArea: currentPen.visual,
          backgroundColor: colors.greyLightSecondary,
          display: "grid",
          padding: "0px 10px",
          gridTemplateColumns: isExpanded ? "1fr 2fr 20px" : "1fr 2fr 20px",
          gridTemplateRows: isExpanded ? "30px 1fr" : "30px",
          gap: "0px 10px",
          borderRadius
        }}>
          {penElement}
          <div style={{ gridArea: isExpanded ? "2/1/3/3" : "1/4/2/5", display: "flex", alignItems: "center", borderTop: isExpanded ? "2px solid "+colors.greyTertiary : "none" }}>
            {isExpanded ? <div style={{display: "flex", flexDirection: "column", gap: "5px", padding: "10px 0px"}}>{popOverElement}</div>
            : (<PrimaryPopover
                content={
                  <div onClick={() => setIsOpen(true)} style={{ cursor: "pointer", display: "flex" }}>
                    <glyphs.Note fillColor={colors.greyDarkPrimary} />
                  </div>
                }
                overrides={{ popoverBgColor: colors.blackPrimary }}
                popoverHeader={`${t("devices.ioSpecifications", {defaultValue: "i/o specifications"})}`}
                popoverContent={
                  <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
                    {popOverElement}
                  </div>
                }
              />)
            }
          </div>
        </div>
      );
    });
  
    return views;
  }, [pens.contents, mobileXs, section.columns, section.rows, t, deviceInfo, isExpanded]);
  // #endregion

  return (
    <div style={{ width: "100%", display: "grid", gridTemplateRows: "20px 2px 1fr 2px", gap: "5px", gridTemplateColumns: "1fr", height: "100%" }}>
      <div style={{ gridArea: "1/1/2/2", display: "flex", alignItems: "end", justifyContent: "flex-end", padding: "0px 22px", boxSizing: "border-box"}}>
        <NewButton
          content={isExpanded ? t("locationFeederPages.sections.collapse", {defaultValue: "collapse"}) : t("locationFeederPages.sections.expand", {defaultValue: "expand"})}
          buttonType={ButtonTypes.textButton}
          endEnhancer={glyphs.Arrow({rotate: isExpanded ? 90 : -90, size: "15px", transparency: isExpanded ? 1 : 0.3 })}
          onClick={() => setExpand(!isExpanded)}
        />
      </div>
      
      <div style={{ gridArea: "2/1/3/2", borderBottom: "2px solid " + colors.blueDarkSecondary }} />
      
      <div style={{
        gridArea: "3/1/4/2",
        display: !mobileXs ? "grid" : "flex",
        flexDirection: "column",
        gridTemplateRows: !mobileXs ? `repeat(${section.rows}, 1fr)` : "none",
        gridTemplateColumns: !mobileXs ? `repeat(${section.columns}, 1fr)` : "none",
        gap: "10px",
        borderRadius: "10px",
        overflowX: "hidden",
        overflowY: "auto",
        padding: "10px",
        height: "100%",
        maxHeight: "40vh",
        boxSizing: "border-box",
        scrollbarWidth: "thin",
        scrollbarColor: colors.blueDarkSecondary + colors.transparent
      }}>
        {penView}
      </div>
      
      <div style={{ gridArea: "4/1/5/2", borderBottom: "2px solid " + colors.blueDarkSecondary }} />
  </div> 
 );
}

export default NewSectionModalLvl3